import React, { useState, useEffect } from 'react';
import { X, Send, Paperclip, User, Mail, Plus, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface EmailRecipient {
  email: string;
  name?: string;
}

interface EmailComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (emailData: {
    to: EmailRecipient[];
    subject: string;
    message: string;
  }) => Promise<void>;
  defaultRecipients?: EmailRecipient[];
  defaultSubject?: string;
  defaultMessage?: string;
  invoicePreview?: React.ReactNode;
  sending?: boolean;
}

const EmailComposeModal: React.FC<EmailComposeModalProps> = ({
  isOpen,
  onClose,
  onSend,
  defaultRecipients = [],
  defaultSubject = '',
  defaultMessage = '',
  invoicePreview,
  sending = false
}) => {
  const [recipients, setRecipients] = useState<EmailRecipient[]>(defaultRecipients);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [newRecipient, setNewRecipient] = useState('');
  const [showRecipientInput, setShowRecipientInput] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRecipients(defaultRecipients);
      setSubject(defaultSubject);
      setMessage(defaultMessage);
      setShowRecipientInput(false);
    }
  }, [isOpen, defaultRecipients, defaultSubject, defaultMessage]);

  const handleAddRecipient = () => {
    if (newRecipient.trim() && isValidEmail(newRecipient.trim())) {
      setRecipients([...recipients, { email: newRecipient.trim() }]);
      setNewRecipient('');
      setShowRecipientInput(false);
    }
  };

  const handleRemoveRecipient = (index: number) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      alert('Please add at least one recipient');
      return;
    }
    if (!subject.trim()) {
      alert('Please enter a subject');
      return;
    }
    if (!message.trim()) {
      alert('Please enter a message');
      return;
    }

    try {
      await onSend({
        to: recipients,
        subject: subject.trim(),
        message: message.trim()
      });
    } catch (error) {
      console.error('Failed to send email:', error);
    }
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddRecipient();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg shadow-2xl w-full max-w-4xl h-full max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)] bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Compose Email</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Email Form */}
          <div className="flex-1 flex flex-col overflow-hidden text-[var(--color-text)]">
            {/* Recipients */}
            <div className="p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center space-x-2 mb-2">
                <User className="w-4 h-4 text-[var(--color-text-secondary)]" />
                <span className="text-sm font-medium text-[var(--color-text)]">To:</span>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-2">
                {recipients.map((recipient, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-1 bg-[var(--color-primary-light)] text-[var(--color-primary)] px-3 py-1 rounded-full text-sm font-medium"
                  >
                    <span>{recipient.name || recipient.email}</span>
                    <button
                      onClick={() => handleRemoveRecipient(index)}
                      className="ml-1 hover:text-red-500 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {showRecipientInput ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyPress={handleKeyPress}
                      onBlur={handleAddRecipient}
                      placeholder="Enter email address"
                      className="flex-1 px-3 py-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-[var(--color-text-secondary)]"
                      autoFocus
                    />
                    <button
                      onClick={handleAddRecipient}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowRecipientInput(true)}
                    className="flex items-center space-x-1 text-blue-500 hover:text-blue-600 text-sm font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add recipient</span>
                  </button>
                )}
              </div>
            </div>

            {/* Subject */}
            <div className="p-4 border-b border-[var(--color-border)]">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-medium text-[var(--color-text)]">Subject:</span>
              </div>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject"
                className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-[var(--color-text-secondary)]"
              />
            </div>

            {/* Message Body */}
            <div className="flex-1 flex flex-col p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--color-text)]">Message:</span>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-background)] rounded transition-colors">
                    <Paperclip className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message here..."
                className="flex-1 w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-[var(--color-text-secondary)]"
                style={{ minHeight: '200px' }}
              />
            </div>

            {/* Invoice Preview */}
            {invoicePreview && (
              <div className="p-4 border-t border-[var(--color-border)] bg-[var(--color-background)]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--color-text)]">Invoice Preview:</span>
                </div>
                <div className="max-h-64 overflow-auto border border-[var(--color-border)] rounded-md bg-[var(--color-surface)]">
                  {invoicePreview}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-[var(--color-border)] bg-[var(--color-background)] rounded-b-lg">
            <div className="flex items-center space-x-2 text-sm text-[var(--color-text-secondary)]">
              <span>Recipients: {recipients.length}</span>
              {recipients.length > 0 && (
                <span>•</span>
              )}
              {recipients.length > 0 && (
                <span>{recipients.map(r => r.email).join(', ')}</span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending || recipients.length === 0 || !subject.trim() || !message.trim()}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
                <span>{sending ? 'Sending...' : 'Send Email'}</span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailComposeModal;
